/**
 * PrivacyHubPage — one nav entry for "is this privacy-safe and compliant?".
 *
 * Disclosures, Consent, IDs & Schain and app-ads.txt were four Tools entries
 * that a QA person reaches for with the same question. They render unchanged
 * as tabs here (each keeps its own padding and layout). The old routes
 * redirect to /privacy?tab=… so links and bookmarks still land on the right tab.
 */
import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import DisclosuresPage from './DisclosuresPage';
import ConsentInspectorPage from './ConsentInspectorPage';
import IdGraphPage from './IdGraphPage';
import AppAdsPage from './AppAdsPage';

export const PRIVACY_TABS = ['disclosures', 'consent', 'ids', 'appads'];

const TAB_LABELS = {
  disclosures: 'Data leaving the device',
  consent: 'Consent',
  ids: 'IDs & supply chain',
  appads: 'app-ads.txt',
};

/** tabFromSearch(search) — the ?tab= deep link, or 'disclosures' when absent/unknown. */
export function tabFromSearch(search) {
  try {
    const t = new URLSearchParams(search || '').get('tab');
    return PRIVACY_TABS.includes(t) ? t : 'disclosures';
  } catch { return 'disclosures'; }
}

export default function PrivacyHubPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const [tab, setTab] = useState(() => tabFromSearch(location.search));

  // A nav to /privacy?tab=… while mounted (an old-route redirect) must switch
  // the tab, not just the URL.
  useEffect(() => {
    if (new URLSearchParams(location.search).has('tab')) setTab(tabFromSearch(location.search));
  }, [location.search]);

  function changeTab(next) {
    setTab(next);
    navigate(`/privacy?tab=${next}`, { replace: true });
  }

  return (
    <Box>
      <Box sx={{ maxWidth: 1240, mx: 'auto', px: 2.5, pt: 2 }}>
        <Tabs
          value={tab}
          onChange={(e, v) => changeTab(v)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          {PRIVACY_TABS.map((t) => <Tab key={t} value={t} label={TAB_LABELS[t]} />)}
        </Tabs>
      </Box>
      {tab === 'disclosures' && <DisclosuresPage />}
      {tab === 'consent' && <ConsentInspectorPage />}
      {tab === 'ids' && <IdGraphPage />}
      {tab === 'appads' && <AppAdsPage />}
    </Box>
  );
}
